import React, { useCallback, useState, useRef } from 'react';
import { formatRUT, parseRUT, validateRUT, validateEmail, formatNumber, parseNumber, calculateCursorPosition } from '../utils/formatters';
import { UserIcon, CreditCardIcon, MailIcon, PhoneIcon, MapPinIcon, HomeIcon, CheckCircle2Icon, RocketIcon } from 'lucide-react';
const CIUDADES = ['Santiago', 'Providencia', 'Las Condes', 'Vitacura', 'Ñuñoa', 'La Reina', 'Peñalolén', 'Maipú', 'Puente Alto', 'Valparaíso', 'Viña del Mar', 'Concepción', 'Temuco', 'Puerto Montt', 'La Serena', 'Antofagasta', 'Otra ciudad'];
export function PreRegistration() {
  // Estados del formulario
  const [nombre, setNombre] = useState('');
  const [rut, setRut] = useState('');
  const [rutDisplay, setRutDisplay] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [propertyValue, setPropertyValue] = useState<number>(0);
  const [propertyDisplay, setPropertyDisplay] = useState('');
  const [touched, setTouched] = useState(false);
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Ref para cursor
  const propertyRef = useRef<HTMLInputElement>(null);
  // Validaciones
  const errors = {
    nombre: touched && nombre.trim().length < 2 ? 'Ingresa tu nombre' : '',
    rut: touched && !validateRUT(rut) ? 'RUT inválido' : '',
    email: touched && !validateEmail(email) ? 'Correo inválido' : '',
    telefono: touched && telefono.trim().length < 8 ? 'Teléfono inválido' : '',
    ciudad: touched && ciudad === '' ? 'Selecciona tu ciudad' : '',
    propertyValue: touched && propertyValue <= 0 ? 'Ingresa el valor estimado' : ''
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const isValid = nombre.trim().length >= 2 && validateRUT(rut) && validateEmail(email) && telefono.trim().length >= 8 && ciudad !== '' && propertyValue > 0;
  // Handlers
  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleaned = parseRUT(value);
    setRut(cleaned);
    setRutDisplay(formatRUT(value));
  };
  const handlePropertyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const cursorStart = input.selectionStart || 0;
    const cursorEnd = input.selectionEnd || 0;
    const oldDisplay = propertyDisplay;
    const newRawValue = input.value;
    if (newRawValue.trim() === '') {
      setPropertyValue(0);
      setPropertyDisplay('');
      return;
    }
    const numericValue = parseNumber(newRawValue);
    setPropertyValue(numericValue);
    const formatted = formatNumber(numericValue);
    setPropertyDisplay(formatted);
    requestAnimationFrame(() => {
      if (propertyRef.current) {
        const newPosition = calculateCursorPosition(oldDisplay, formatted, cursorStart, cursorEnd);
        propertyRef.current.setSelectionRange(newPosition, newPosition);
      }
    });
  }, [propertyDisplay]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    // Simular envío
    const payload = {
      nombre: nombre.trim(),
      rut: formatRUT(rut),
      email: email.trim(),
      telefono: telefono.trim(),
      ciudad,
      propertyValue,
      ts: new Date().toISOString(),
      type: 'pre-registration'
    };
    console.log('PRE_REGISTRATION_SUBMIT', payload);
    // Dispatch event para tracking
    window.dispatchEvent(new CustomEvent('pre-registration-submit', {
      detail: payload
    }));
    // Simular delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setSent(true);
  };
  if (sent) {
    return <section id="pre-registro" className="py-12 md:py-24 bg-gradient-to-b from-[#E8EDF5] to-white" data-id="element-1477">
        <div className="container mx-auto max-w-2xl px-4" data-id="element-1478">
          <div className="bg-white rounded-2xl border-2 border-[#2B5FD9] p-8 md:p-12 shadow-xl text-center" data-id="element-1479">
            <div className="mb-6 flex justify-center" data-id="element-1480">
              <div className="rounded-full bg-[#2B5FD9]/10 p-4" data-id="element-1481">
                <CheckCircle2Icon className="h-12 w-12 text-[#2B5FD9]" data-id="element-1482" />
              </div>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4" data-id="element-1483">
              ¡Gracias, {nombre}!
            </h3>
            <p className="text-lg text-[#6B7280] mb-2" data-id="element-1484">
              Tu pre-registro fue recibido correctamente.
            </p>
            <p className="text-base text-[#6B7280] mb-6" data-id="element-1485">
              Te contactaremos a <strong data-id="element-1486">{email}</strong> cuando tu propiedad
              sea elegible para nuestro programa.
            </p>
            <div className="bg-[#E8EDF5] rounded-xl p-4" data-id="element-1487">
              <p className="text-sm text-[#6B7280]" data-id="element-1488">
                Mientras tanto, puedes escribirnos a{' '}
                <a href="mailto:hola@creditual.com" className="text-[#2B5FD9] font-medium hover:underline" data-id="element-1489">
                  hola@creditual.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>;
  }
  return <section id="pre-registro" className="py-12 md:py-24 bg-gradient-to-b from-[#E8EDF5] to-white" data-id="element-1490">
      <div className="container mx-auto max-w-3xl px-4" data-id="element-1491">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12" data-id="element-1492">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-200 rounded-full mb-4" data-id="element-1493">
            <RocketIcon className="h-4 w-4 text-amber-700" data-id="element-1494" />
            <span className="text-sm font-semibold text-amber-700" data-id="element-1495">
              Próximamente
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4 text-[#0A1628]" data-id="element-1496">
            Estamos lanzando oficialmente pronto
          </h2>
          <p className="text-sm md:text-lg text-[#6B7280] max-w-2xl mx-auto" data-id="element-1497">
            Déjanos tus datos y te contactaremos cuando tu propiedad sea
            elegible para nuestro programa.
          </p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8" data-id="element-1498">
          <div className="grid md:grid-cols-2 gap-5" data-id="element-1499">
            {/* Nombre */}
            <div data-id="element-1500">
              <label className="block text-sm font-medium mb-2" data-id="element-1501">
                Nombre completo *
              </label>
              <div className="relative" data-id="element-1502">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1503" />
                <input type="text" value={nombre} onChange={e => setNombre(e.target.value)} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="Tu nombre y apellido" data-id="element-1504" />
              </div>
              {errors.nombre && <p className="mt-1 text-xs text-red-600" data-id="element-1505">{errors.nombre}</p>}
            </div>

            {/* RUT */}
            <div data-id="element-1506">
              <label className="block text-sm font-medium mb-2" data-id="element-1507">RUT *</label>
              <div className="relative" data-id="element-1508">
                <CreditCardIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1509" />
                <input type="text" value={rutDisplay} onChange={handleRutChange} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="12.345.678-9" data-id="element-1510" />
              </div>
              {errors.rut && <p className="mt-1 text-xs text-red-600" data-id="element-1511">{errors.rut}</p>}
            </div>

            {/* Email */}
            <div data-id="element-1512">
              <label className="block text-sm font-medium mb-2" data-id="element-1513">
                Correo electrónico *
              </label>
              <div className="relative" data-id="element-1514">
                <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1515" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="tu@correo.cl" data-id="element-1516" />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1517">{errors.email}</p>}
            </div>

            {/* Teléfono */}
            <div data-id="element-1518">
              <label className="block text-sm font-medium mb-2" data-id="element-1519">
                Teléfono *
              </label>
              <div className="relative" data-id="element-1520">
                <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1521" />
                <input type="tel" value={telefono} onChange={e => setTelefono(e.target.value)} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="+56 9 1234 5678" data-id="element-1522" />
              </div>
              {errors.telefono && <p className="mt-1 text-xs text-red-600" data-id="element-1523">{errors.telefono}</p>}
            </div>

            {/* Ciudad */}
            <div data-id="element-1524">
              <label className="block text-sm font-medium mb-2" data-id="element-1525">Ciudad *</label>
              <div className="relative" data-id="element-1526">
                <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1527" />
                <select value={ciudad} onChange={e => setCiudad(e.target.value)} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20 bg-white appearance-none" data-id="element-1528">
                  <option value="" data-id="element-1529">Selecciona tu ciudad</option>
                  {CIUDADES.map(c => <option key={c} value={c} data-id="element-1530">
                      {c}
                    </option>)}
                </select>
              </div>
              {errors.ciudad && <p className="mt-1 text-xs text-red-600" data-id="element-1531">{errors.ciudad}</p>}
            </div>

            {/* Valor propiedad */}
            <div data-id="element-1532">
              <label className="block text-sm font-medium mb-2" data-id="element-1533">
                Valor estimado propiedad (UF) *
              </label>
              <div className="relative" data-id="element-1534">
                <HomeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1535" />
                <input ref={propertyRef} type="text" inputMode="numeric" value={propertyDisplay} onChange={handlePropertyChange} onBlur={() => setTouched(true)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="4.500" data-id="element-1536" />
              </div>
              {errors.propertyValue && <p className="mt-1 text-xs text-red-600" data-id="element-1537">
                  {errors.propertyValue}
                </p>}
            </div>
          </div>

          {/* Submit */}
          <button type="submit" disabled={!isValid || isSubmitting} className="w-full mt-6 px-6 py-4 bg-[#2B5FD9] text-white font-semibold rounded-xl hover:bg-[#1E4BB8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1538">
            {isSubmitting ? 'Enviando...' : 'Pre-registrarme'}
          </button>

          <p className="text-xs text-center text-[#6B7280] mt-4" data-id="element-1539">
            Al enviar, aceptas nuestros{' '}
            <a href="/terminos" className="text-[#2B5FD9] hover:underline" data-id="element-1540">
              términos y condiciones
            </a>{' '}
            y{' '}
            <a href="/privacidad" className="text-[#2B5FD9] hover:underline" data-id="element-1541">
              política de privacidad
            </a>
            .
          </p>
        </form>
      </div>
    </section>;
}